import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Image,
  Svg,
  Path,
  G,
} from "@react-pdf/renderer";
import { getFormattedTransactionId } from "utils/utilityFunctions";
import { getOrganizationLogoUrl } from "utils/organizationLogoCache";

const BRAND = {
  primary: "#4B1E78",       // SiteMaster primary purple
  primaryLight: "#7B4DB3",
  primarySoft: "#F7F2FC",
  text: "#1E1B24",
  textSoft: "#6B6575",
  border: "#E7DDF4",
  rowBorder: "#F1EAF9",
  success: "#1F8A4C",
  white: "#FFFFFF",
};

const styles = StyleSheet.create({
  page: {
    position: "relative",
    paddingTop: 24,
    paddingRight: 30,
    paddingBottom: 55,
    paddingLeft: 30,
    backgroundColor: BRAND.white,
    fontSize: 9.5,
    fontFamily: "Helvetica",
    color: BRAND.text,
    lineHeight: 1.45,
  },

  topRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-end",
    marginBottom: 15,
  },

  logoShell: {
    height: 70,
    width: 70
  },

  logoImage: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
  },

  headerLine: {
    flexGrow: 1,
    borderTopWidth: 1.5,
    borderTopColor: BRAND.primary,
    marginLeft: 12,
  },

  orgBlock: {
    alignItems: "flex-end",
    maxWidth: "70%",
  },

  orgName: {
    fontSize: 14,
    fontWeight: "bold",
    color: BRAND.primary,
    marginBottom: 2,
    textAlign: "right",
  },

  orgSubText: {
    fontSize: 8.5,
    color: BRAND.textSoft,
    textAlign: "right",
    marginBottom: 1,
  },

  titleBlock: {
    marginTop: 2,
    marginBottom: 6,
  },

  titleRow: {
    flexDirection: "row",
    alignItems: "flex-end",
    borderBottomWidth: 1.5,
    borderBottomColor: BRAND.primary,
    paddingBottom: 1.5,
  },

  titleText: {
    fontSize: 14,
    fontWeight: "semibold",
    color: BRAND.primary,
    letterSpacing: 0.8,
  },

  metaBlock: {
    marginTop: 6,
    marginBottom: 8,
  },

  metaLine: {
    fontSize: 10.5,
    color: BRAND.text,
    marginBottom: 4,
  },
  metaLabel: {
    fontWeight: 600,
    fontSize: 10.5,
  },
  metaLineView: {
    flexDirection: "row"
  },
  metaValue: {
    flexGrow: 1,
    fontSize: 10.5,
    fontWeight: "normal",
    marginLeft: 4,
  },
  sectionBlock: {
    marginTop: 4,
    marginBottom: 10,
  },

  sectionLabel: {
    fontSize: 10.5,
    fontWeight: "bold",
    color: BRAND.primary,
    marginBottom: 5,
    borderBottomWidth: 1,
    borderBottomColor: BRAND.border,
  },

  paragraphText: {
    fontSize: 10,
    color: BRAND.text,
    marginBottom: 3,
    lineHeight: 1.4,
  },

  tableTitle: {
    fontSize: 10,
    fontWeight: "bold",
    color: BRAND.primary,
    marginBottom: 6,
  },

  table: {
    borderWidth: 1,
    borderColor: BRAND.border,
    marginBottom: 10,
  },

  tableHeaderRow: {
    flexDirection: "row",
    backgroundColor: BRAND.primarySoft,
    borderBottomWidth: 1,
    borderBottomColor: BRAND.border,
  },

  tableRow: {
    flexDirection: "row",
    borderBottomWidth: 1,
    borderBottomColor: BRAND.rowBorder,
  },

  tableRowLast: {
    borderBottomWidth: 0,
  },
  alignCenter: { textAlign: "center" },
  alignRight: { textAlign: "right" },
  cellSmall: { width: "12%", paddingHorizontal: 4, paddingVertical: 2 },
  cellMedium: { width: "18%", paddingHorizontal: 4, paddingVertical: 2 },
  cellMaterial: { width: "40%", paddingHorizontal: 4, paddingVertical: 2 },
  cellContractorScope: { width: "45%", paddingHorizontal: 4, paddingVertical: 2 },
  cellContractorQty: { width: "15%", paddingHorizontal: 4, paddingVertical: 2 },
  cellContractorPrice: { width: "20%", paddingHorizontal: 4, paddingVertical: 2 },
  cellContractorTotal: { width: "20%", paddingHorizontal: 4, paddingVertical: 2 },

  headerCellText: {
    fontSize: 9.2,
    fontWeight: "bold",
    color: BRAND.primary,
  },

  cellText: {
    fontSize: 9.5,
    color: BRAND.text,
  },

  subCellText: {
    fontSize: 8,
    color: BRAND.textSoft,
    marginTop: 2,
  },

  summaryBox: {
    borderWidth: 1,
    borderColor: BRAND.border,
    backgroundColor: BRAND.primarySoft,
    paddingTop: 8,
    paddingRight: 10,
    paddingBottom: 8,
    paddingLeft: 10,
    marginBottom: 12,
  },

  summaryHeading: {
    fontSize: 10,
    fontWeight: "bold",
    color: BRAND.primary,
    marginBottom: 8,
    borderBottomWidth: 1,
    borderBottomColor: BRAND.border,
  },

  summaryRow: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 5,
  },

  summaryLabel: {
    fontSize: 9.3,
    color: BRAND.textSoft,
  },

  summaryValue: {
    fontSize: 9.3,
    color: BRAND.text,
    fontWeight: "bold",
  },

  summaryDivider: {
    borderTopWidth: 1,
    borderTopColor: BRAND.border,
    marginTop: 4,
    marginBottom: 6,
  },

  summaryStrong: {
    fontSize: 10.5,
    fontWeight: "bold",
    color: BRAND.primary,
  },

  totalPaid: {
    fontSize: 13,
    fontWeight: "bold",
    color: BRAND.text,
    marginBottom: 12,
  },

  remarksText: {
    fontSize: 10,
    color: BRAND.text,
    lineHeight: 1.45,
  },

  pill: {
    borderWidth: 1,
    borderColor: BRAND.primaryLight,
    borderRadius: 4,
    paddingTop: 3,
    paddingRight: 6,
    paddingBottom: 3,
    paddingLeft: 6,
    alignSelf: "flex-start",
    backgroundColor: BRAND.primarySoft,
  },

  footer: {
    position: "absolute",
    left: 30,
    right: 30,
    bottom: 20,
    borderTopWidth: 1,
    borderTopColor: BRAND.border,
    paddingTop: 8,
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },

  footerText: {
    fontSize: 8.8,
    color: BRAND.textSoft,
  },
  footerLine: {
    flexDirection: "row",
    alignItems: "center",
    marginBottom: 4,
  },
  footerIcon: {
    width: 8,
    height: 8,
    marginRight: 4,
  },
  footerLeft: {
    flex: 1,
  },
  footerRight: {
    flex: 1,
    alignItems: "flex-end",
  },

  attachmentPage: {
    padding: 0,
    margin: 0,
    backgroundColor: BRAND.white,
  },

  attachmentImage: {
    width: "100%",
    height: "100%",
    objectFit: "contain",
  },
});

const PAYMENT_STRUCTURE_LABELS = {
  MILESTONE_BASED: "Milestone",
  MONTHLY_RUNNING_BILL: "Monthly",
  LUMPSUM_CUSTOM: "Custom",
  FIXED_PRICE: "Custom",
  Milestone: "Milestone",
  Monthly: "Monthly",
  Lumpsum: "Custom",
  "Fixed Price": "Custom",
};

const getSafeLocalObject = (key) => {
  try {
    const value = localStorage.getItem(key);
    return value ? JSON.parse(value) : {};
  } catch (error) {
    return {};
  }
};

const toDisplayString = (value, fallback = "N/A") => {
  if (value === null || value === undefined) return fallback;
  const text = String(value).trim();
  return text ? text : fallback;
};

const EmailIcon = () => (
  <Svg viewBox="0 0 24 24" style={styles.footerIcon}>
    <Path
      d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"
      stroke={BRAND.textSoft}
      strokeWidth={2}
      fill="none"
    />
    <Path d="m22 6-10 7L2 6" stroke={BRAND.textSoft} strokeWidth={2} fill="none" />
  </Svg>
);

const PhoneIcon = () => (
  <Svg viewBox="0 0 24 24" style={styles.footerIcon}>
    <Path
      d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"
      stroke={BRAND.textSoft}
      strokeWidth={2}
      fill="none"
    />
  </Svg>
);

const WebIcon = () => (
  <Svg viewBox="0 0 24 24" style={styles.footerIcon}>
    <Path
      d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"
      stroke={BRAND.textSoft}
      strokeWidth={2}
      fill="none"
    />
    <Path d="M12 8a4 4 0 1 0 0 8 4 4 0 0 0 0-8z" stroke={BRAND.textSoft} strokeWidth={2} fill="none" />
  </Svg>
);

const toNumber = (value) => {
  const num = Number(value);
  return Number.isFinite(num) ? num : 0;
};

const formatCurrency = (value) => {
  return `Rs. ${toNumber(value).toLocaleString("en-IN")}`;
};

const formatDate = (value) => {
  if (!value) return "N/A";
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return "N/A";
  return date.toLocaleDateString("en-IN", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
};

const buildAddress = (...parts) => {
  return parts
    .flat()
    .filter((part) => part !== null && part !== undefined && String(part).trim())
    .map((part) => String(part).trim())
    .join(", ");
};

const getProjectInfo = ({ transactionData, scope, siteData }) => {
  if (scope === "project") {
    return {
      name: toDisplayString(
        siteData?.site_name || siteData?.name || transactionData?.site_name,
      ),
      address: toDisplayString(
        buildAddress(
          siteData?.address,
          siteData?.city,
          siteData?.state,
          siteData?.pincode,
        ),
      ),
    };
  }

  return {
    name: toDisplayString(
      transactionData?.site_name || transactionData?.projectName || transactionData?.project_name,
    ),
    address: toDisplayString(
      transactionData?.site_address ||
      transactionData?.project_address ||
      buildAddress(
        transactionData?.city,
        transactionData?.state,
        transactionData?.pincode,
      ),
    ),
  };
};

const getReceiptTitle = (type) => {
  switch (type) {
    case "material":
      return "Material Payment Invoice";
    case "contractor":
      return "Work Order Payment Invoice";
    case "worker":
      return "Worker Payment Invoice";
    case "received":
      return "Received Payment Invoice";
    case "refund":
      return "Refund Payment Invoice";
    case "vendor":
      return "Advance Payment Invoice";
    case "other":
    default:
      return "General Payment Invoice";
  }
};

const getPaymentMethod = (transactionData) => {
  return toDisplayString(
    transactionData?.payment_method ||
    transactionData?.transaction_method ||
    transactionData?.payment_mode ||
    transactionData?.method ||
    transactionData?.medium ||
    transactionData?.mode,
  );
};

const getPaidBy = (transactionData) => {
  return toDisplayString(
    transactionData?.paid_by ||
    transactionData?.created_by ||
    transactionData?.paid_by_name ||
    transactionData?.user_name,
  );
};

const getPaymentDate = (transactionData) => {
  return formatDate(
    transactionData?.payment_date ||
    transactionData?.transaction_date ||
    transactionData?.date ||
    transactionData?.created_on,
  );
};

const getStatusText = (transactionData) => {
  return toDisplayString(
    transactionData?.payment_status ||
    transactionData?.transaction_status ||
    transactionData?.status,
  );
};

const getRemarks = (transactionData) => {
  return toDisplayString(
    transactionData?.remarks ||
    transactionData?.comments ||
    transactionData?.comment ||
    transactionData?.description,
    "",
  );
};

const getVendorAddress = (transactionData) => {
  return toDisplayString(
    transactionData?.vendor_address || transactionData?.address,
  );
};

const getWorkerAddress = (transactionData) => {
  return toDisplayString(
    transactionData?.worker_address || transactionData?.address,
  );
};

const getContractorAddress = (transactionData) => {
  return toDisplayString(
    transactionData?.contractor_address || transactionData?.contractor?.address,
  );
};

const getRefundPurpose = (transactionData) => {
  return toDisplayString(
    transactionData?.refund_purpose ||
    transactionData?.refundPurpose ||
    transactionData?.refund_reason,
  );
};

const getPaymentStructure = (transactionData) => {
  const rawValue =
    transactionData?.payment_structure ||
    transactionData?.payment_type ||
    transactionData?.type;

  return toDisplayString(PAYMENT_STRUCTURE_LABELS[rawValue] || rawValue);
};

const getWorkOrderLabel = (transactionData) => {
  const rawValue =
    transactionData?.work_order_no ||
    transactionData?.work_order_formatted_id ||
    transactionData?.work_order ||
    transactionData?.description;

  if (!rawValue) return "N/A";
  return String(rawValue).startsWith("WO") ? String(rawValue) : `WO-${rawValue}`;
};

const getAttachmentImages = (transactionData) => {
  const rawImages = [
    transactionData?.receipt_image,
    transactionData?.receipt_url,
    transactionData?.ireceipt_image,
    transactionData?.attachment_file,
    transactionData?.bill_image,
  ].flatMap((item) => {
    if (!item) return [];
    return Array.isArray(item) ? item : [item];
  });

  return [...new Set(rawImages.filter(Boolean))];
};

const getReceiptIdentifier = (transactionData, type) => {
  return toDisplayString(
    transactionData?.receipt_id ||
    getFormattedTransactionId(transactionData, type) ||
    transactionData?.transaction_id ||
    transactionData?.id,
  );
};

const MetaLine = ({ label, value }) => (
  <View style={styles.metaLineView}>
    <Text style={styles.metaLabel}>{label}: </Text>
    <Text style={styles.metaValue}>{toDisplayString(value)}</Text>
  </View>
);

const SummaryRows = ({ rows, strongRows = [] }) => (
  <View style={styles.summaryBox}>
    <Text style={styles.summaryHeading}>Financial Summary</Text>
    {rows.map((row, index) => (
      <View key={`${row.label}-${index}`}>
        <View style={styles.summaryRow}>
          <Text style={styles.summaryLabel}>{row.label}</Text>
          <Text style={styles.summaryValue}>{row.value}</Text>
        </View>
      </View>
    ))}
    {strongRows.length > 0 && <View style={styles.summaryDivider} />}
    {strongRows.map((row, index) => (
      <View key={`${row.label}-${index}`} style={styles.summaryRow}>
        <Text style={styles.summaryStrong}>{row.label}</Text>
        <Text style={styles.summaryStrong}>{row.value}</Text>
      </View>
    ))}
  </View>
);

const MaterialItemsTable = ({ materials }) => {
  if (!Array.isArray(materials) || materials.length === 0) return null;

  return (
    <View style={styles.sectionBlock}>
      <Text style={styles.tableTitle}>Items / Materials</Text>
      <View style={styles.table}>
        <View style={styles.tableHeaderRow}>
          <View style={styles.cellMaterial}>
            <Text style={styles.headerCellText}>Material</Text>
          </View>
          <View style={styles.cellSmall}>
            <Text style={[styles.headerCellText, styles.alignCenter]}>Qty</Text>
          </View>
          <View style={styles.cellSmall}>
            <Text style={[styles.headerCellText, styles.alignCenter]}>Price</Text>
          </View>
          <View style={styles.cellSmall}>
            <Text style={[styles.headerCellText, styles.alignCenter]}>Tax</Text>
          </View>
          <View style={styles.cellMedium}>
            <Text style={[styles.headerCellText, styles.alignRight]}>Total</Text>
          </View>
        </View>
        {materials.map((item, index) => {
          const isLast = index === materials.length - 1;
          return (
            <View
              key={`${item?.id || item?.material_name || "material"}-${index}`}
              style={[styles.tableRow, isLast && styles.tableRowLast]}
            >
              <View style={styles.cellMaterial}>
                <Text style={styles.cellText}>
                  {toDisplayString(item?.material_name || item?.name)}
                </Text>
                {item?.material_subcategory || item?.category ? (
                  <Text style={styles.subCellText}>
                    {toDisplayString(item?.material_subcategory || item?.category)}
                  </Text>
                ) : null}
              </View>
              <View style={styles.cellSmall}>
                <Text style={[styles.cellText, styles.alignCenter]}>
                  {toDisplayString(
                    `${item?.quantity ?? item?.qty ?? 0} ${item?.unit || item?.unit_type || ""
                      }`.trim(),
                  )}
                </Text>
              </View>
              <View style={styles.cellSmall}>
                <Text style={[styles.cellText, styles.alignCenter]}>
                  {toNumber(item?.rate || item?.unit_price).toLocaleString("en-IN")}
                </Text>
              </View>
              <View style={styles.cellSmall}>
                <Text style={[styles.cellText, styles.alignCenter]}>
                  {toNumber(item?.tax_amount).toLocaleString("en-IN")}
                </Text>
              </View>
              <View style={styles.cellMedium}>
                <Text style={[styles.cellText, styles.alignRight]}>
                  {toNumber(item?.total_amount || item?.total_price || item?.price).toLocaleString(
                    "en-IN",
                  )}
                </Text>
              </View>
            </View>
          );
        })}
      </View>
    </View>
  );
};

const ContractorItemsTable = ({ scopeItems }) => {
  if (!Array.isArray(scopeItems) || scopeItems.length === 0) return null;

  return (
    <View style={styles.sectionBlock}>
      <Text style={styles.tableTitle}>Items / Scope of Work</Text>
      <View style={styles.table}>
        <View style={styles.tableHeaderRow}>
          <View style={styles.cellContractorScope}>
            <Text style={styles.headerCellText}>Scope Name</Text>
          </View>
          <View style={styles.cellContractorQty}>
            <Text style={[styles.headerCellText, styles.alignCenter]}>Qty</Text>
          </View>
          <View style={styles.cellContractorPrice}>
            <Text style={[styles.headerCellText, styles.alignCenter]}>Unit Price</Text>
          </View>
          <View style={styles.cellContractorTotal}>
            <Text style={[styles.headerCellText, styles.alignRight]}>Total</Text>
          </View>
        </View>
        {scopeItems.map((item, index) => {
          const isLast = index === scopeItems.length - 1;
          const qtyText = `${item?.quantity ?? 0} ${item?.unit || ""}`.trim();

          return (
            <View
              key={`${item?.id || item?.item || "scope"}-${index}`}
              style={[styles.tableRow, isLast && styles.tableRowLast]}
            >
              <View style={styles.cellContractorScope}>
                <Text style={styles.cellText}>
                  {toDisplayString(item?.item || item?.scope_name)}
                </Text>
                {item?.description ? (
                  <Text style={styles.subCellText}>{toDisplayString(item.description)}</Text>
                ) : null}
              </View>
              <View style={styles.cellContractorQty}>
                <Text style={[styles.cellText, styles.alignCenter]}>
                  {toDisplayString(qtyText)}
                </Text>
              </View>
              <View style={styles.cellContractorPrice}>
                <Text style={[styles.cellText, styles.alignCenter]}>
                  {toNumber(item?.unit_price).toLocaleString("en-IN")}
                </Text>
              </View>
              <View style={styles.cellContractorTotal}>
                <Text style={[styles.cellText, styles.alignRight]}>
                  {toNumber(item?.total_price).toLocaleString("en-IN")}
                </Text>
              </View>
            </View>
          );
        })}
      </View>
    </View>
  );
};

const ProjectBlock = ({ projectName, projectAddress, showProject }) => {
  if (!showProject) return null;

  return (
    <>
      {projectName && <MetaLine label="Project" value={projectName} />}
      {projectAddress && <MetaLine label="Project Address" value={projectAddress} />}
    </>
  );
};

const BilledToBlock = ({ title, lines }) => (
  <View style={styles.sectionBlock}>
    <Text style={styles.sectionLabel}>{title}</Text>
    {lines
      .filter((line) => line !== null && line !== undefined && String(line).trim())
      .map((line, index) => (
        <Text key={`${title}-${index}`} style={styles.paragraphText}>
          {String(line)}
        </Text>
      ))}
  </View>
);

const ReceiptPDFDocument = ({
  transactionData = {},
  type = "other",
  scope = "organization",
  organizationLogoSrc = null,
}) => {
  const organization = getSafeLocalObject("organization");
  const siteData = getSafeLocalObject("siteData");
  const projectInfo = getProjectInfo({ transactionData, scope, siteData });
  const organizationLogo = organizationLogoSrc || getOrganizationLogoUrl(organization);
  const receiptNumber = getReceiptIdentifier(transactionData, type);
  const invoiceDate = formatDate(new Date());
  const paymentDate = getPaymentDate(transactionData);
  const paymentMethod = getPaymentMethod(transactionData);
  const statusText = getStatusText(transactionData);
  const remarks = getRemarks(transactionData);
  const attachmentImages = getAttachmentImages(transactionData);
  const amountValue = toNumber(
    transactionData?.paid_amount || transactionData?.amount || transactionData?.total_amount,
  );
  const materialItems = Array.isArray(transactionData?.all_materials)
    ? transactionData.all_materials
    : [];
  const materialTax = materialItems.reduce(
    (total, item) => total + toNumber(item?.tax_amount),
    0,
  );
  const materialTotal =
    toNumber(transactionData?.total_price) ||
    materialItems.reduce(
      (total, item) => total + toNumber(item?.total_amount || item?.total_price || item?.price),
      0,
    );
  const contractorScopeItems = Array.isArray(transactionData?.scope_items)
    ? transactionData.scope_items
    : [];
  const contractorSubtotal =
    contractorScopeItems.reduce((total, item) => total + toNumber(item?.total_price), 0) ||
    amountValue;
  const refundOriginalAmount = toNumber(
    transactionData?.original_transaction_amount ||
    transactionData?.original_amount ||
    transactionData?.amount,
  );

  const showProjectDetails = type !== "vendor";

  const renderBody = () => {
    switch (type) {
      case "material":
        return (
          <>
            <ProjectBlock
              projectName={projectInfo.name}
              projectAddress={projectInfo.address}
              showProject={showProjectDetails}
            />
            <BilledToBlock
              title="Billed To"
              lines={[
                transactionData?.vendor_name,
                transactionData?.vendor_type,
                `Address: ${getVendorAddress(transactionData)}`,
              ]}
            />
            <MaterialItemsTable materials={materialItems} />
            <SummaryRows
              rows={[
                {
                  label: "Sub Total (Excl. Tax)",
                  value: formatCurrency(Math.max(materialTotal - materialTax, 0)),
                },
                {
                  label: "Total Tax",
                  value: formatCurrency(materialTax),
                },
              ]}
              strongRows={[
                { label: "Total Amount", value: formatCurrency(materialTotal) },
                {
                  label: "Paid Amount",
                  value: formatCurrency(
                    transactionData?.paid_amount || 0,
                  ),
                },
              ]}
            />
            <Text style={styles.totalPaid}>
              Total Paid: {formatCurrency(transactionData?.paid_amount || transactionData?.amount || materialTotal)}
            </Text>
            <View style={styles.sectionBlock}>
              <Text style={styles.sectionLabel}>Payment Details:</Text>
              <Text style={styles.paragraphText}>Payment Method : {paymentMethod}</Text>
              <Text style={styles.paragraphText}>Payment Date: {paymentDate}</Text>
              <Text style={styles.paragraphText}>Status: {statusText}</Text>
            </View>
          </>
        );
      case "contractor":
        return (
          <>
            <MetaLine label="Work Order" value={getWorkOrderLabel(transactionData)} />
            <ProjectBlock
              projectName={projectInfo.name}
              projectAddress={projectInfo.address}
              showProject={showProjectDetails}
            />
            <BilledToBlock
              title="Billed To"
              lines={[
                transactionData?.contractor_name ||
                transactionData?.vendor_name ||
                transactionData?.contractor?.name,
                transactionData?.contractor_work_name ||
                transactionData?.contractor?.work_name ||
                transactionData?.contractor?.other_work_name ||
                `Work Category: ${transactionData?.work_name}`,
                `Address: ${getContractorAddress(transactionData)}`,
              ]}
            />
            <ContractorItemsTable scopeItems={contractorScopeItems} />
            <SummaryRows
              rows={[
                {
                  label: "Sub Total (Excl. Tax)",
                  value: formatCurrency(contractorSubtotal),
                },
              ]}
              strongRows={[
                { label: "Total Amount", value: formatCurrency(contractorSubtotal) },
                { label: "Paid Amount", value: formatCurrency(amountValue) },
              ]}
            />
            <Text style={styles.totalPaid}>
              Total Paid: {formatCurrency(amountValue)}
            </Text>
            <View style={styles.sectionBlock}>
              <Text style={styles.sectionLabel}>Payment Details:</Text>
              <Text style={styles.paragraphText}>Payment Method : {paymentMethod}</Text>
              <Text style={styles.paragraphText}>Payment Date: {paymentDate}</Text>
              {/* <Text style={styles.paragraphText}>
                Payment Type: {getPaymentStructure(transactionData)}
              </Text> */}
              {transactionData?.milestone_name ? (
                <Text style={styles.paragraphText}>
                  Milestone: {toDisplayString(transactionData.milestone_name)}
                </Text>
              ) : null}
              {transactionData?.month_name ? (
                <Text style={styles.paragraphText}>
                  Month: {toDisplayString(transactionData.month_name)}
                </Text>
              ) : null}
            </View>
          </>
        );
      case "worker":
        return (
          <>
            <ProjectBlock
              projectName={projectInfo.name}
              projectAddress={projectInfo.address}
              showProject={showProjectDetails}
            />
            <BilledToBlock
              title="Billed To"
              lines={[
                transactionData?.worker_name,
                `Work Category: ${transactionData?.worker_workname}`,
                transactionData?.worker_phone || transactionData?.worker_contact,
                `Address: ${getWorkerAddress(transactionData)}`,
              ]}
            />
            <SummaryRows
              rows={[{ label: "Total Paid Amount", value: formatCurrency(amountValue) }]}
              strongRows={[{ label: "Grand Total", value: formatCurrency(amountValue) }]}
            />
            <Text style={styles.totalPaid}>Total Paid: {formatCurrency(amountValue)}</Text>
            <View style={styles.sectionBlock}>
              <Text style={styles.sectionLabel}>Payment Details:</Text>
              <Text style={styles.paragraphText}>Payment Method : {paymentMethod}</Text>
              <Text style={styles.paragraphText}>Payment Date: {paymentDate}</Text>
            </View>
          </>
        );
      case "received":
        return (
          <>
            <ProjectBlock
              projectName={projectInfo.name}
              projectAddress={projectInfo.address}
              showProject={showProjectDetails}
            />
            <BilledToBlock
              title="Received For"
              lines={[transactionData?.description || transactionData?.transaction_type]}
            />
            <SummaryRows
              rows={[{ label: "Total Received Amount", value: formatCurrency(amountValue) }]}
              strongRows={[{ label: "Grand Total", value: formatCurrency(amountValue) }]}
            />
            <Text style={styles.totalPaid}>Total Received: {formatCurrency(amountValue)}</Text>
            <View style={styles.sectionBlock}>
              <Text style={styles.sectionLabel}>Payment Details:</Text>
              <Text style={styles.paragraphText}>Payment Method : {paymentMethod}</Text>
              <Text style={styles.paragraphText}>Payment Date: {paymentDate}</Text>
            </View>
          </>
        );
      case "refund":
        return (
          <>
            <ProjectBlock
              projectName={projectInfo.name}
              projectAddress={projectInfo.address}
              showProject={showProjectDetails}
            />
            <BilledToBlock
              title="Refund Details"
              lines={[
                `Original transaction type: ${toDisplayString(transactionData?.original_transaction_type)}`,
                `Refund Purpose: ${getRefundPurpose(transactionData)}`,
              ]}
            />
            <SummaryRows
              rows={[
                {
                  label: "Original Transaction Amount",
                  value: formatCurrency(refundOriginalAmount),
                },
                {
                  label: "Total Refund Amount",
                  value: formatCurrency(amountValue),
                },
              ]}
              strongRows={[{ label: "Total Refund", value: formatCurrency(amountValue) }]}
            />
            <Text style={styles.totalPaid}>Total Refund: {formatCurrency(amountValue)}</Text>
            <View style={styles.sectionBlock}>
              <Text style={styles.sectionLabel}>Payment Details:</Text>
              <Text style={styles.paragraphText}>Payment Method : {paymentMethod}</Text>
              <Text style={styles.paragraphText}>Refund Date: {paymentDate}</Text>
              <Text style={styles.paragraphText}>Refund Status: {statusText}</Text>
            </View>
          </>
        );
      case "vendor":
        return (
          <>
            <BilledToBlock
              title="Billed To"
              lines={[
                transactionData?.vendor_name,
                transactionData?.vendor_type,
                transactionData?.vendor_contact || transactionData?.contact_no,
                `Address: ${getVendorAddress(transactionData)}`,
              ]}
            />
            <SummaryRows
              rows={[{ label: "Total Paid Amount", value: formatCurrency(amountValue) }]}
              strongRows={[{ label: "Grand Total", value: formatCurrency(amountValue) }]}
            />
            <Text style={styles.totalPaid}>Total Paid: {formatCurrency(amountValue)}</Text>
            <View style={styles.sectionBlock}>
              <Text style={styles.sectionLabel}>Payment Details:</Text>
              <Text style={styles.paragraphText}>Payment Method : {paymentMethod}</Text>
              <Text style={styles.paragraphText}>Payment Date: {paymentDate}</Text>
              {getPaidBy(transactionData) !== "N/A" && (
                <Text style={styles.paragraphText}>Paid By: {getPaidBy(transactionData)}</Text>
              )}
            </View>
          </>
        );
      case "other":
      default:
        return (
          <>
            <ProjectBlock
              projectName={projectInfo.name}
              projectAddress={projectInfo.address}
              showProject={showProjectDetails}
            />
            <BilledToBlock
              title="Billed For"
              lines={[transactionData?.description || transactionData?.transaction_type]}
            />
            <SummaryRows
              rows={[{ label: "Total Paid Amount", value: formatCurrency(amountValue) }]}
              strongRows={[{ label: "Grand Total", value: formatCurrency(amountValue) }]}
            />
            <Text style={styles.totalPaid}>Total Paid: {formatCurrency(amountValue)}</Text>
            <View style={styles.sectionBlock}>
              <Text style={styles.sectionLabel}>Payment Details:</Text>
              <Text style={styles.paragraphText}>Payment Method : {paymentMethod}</Text>
              <Text style={styles.paragraphText}>Payment Date: {paymentDate}</Text>
            </View>
          </>
        );
    }
  };

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        {/* <View fixed style={styles.pageBorder} /> */}

        <View style={styles.topRow}>
          <View style={styles.logoShell}>
            {organizationLogo && (
              <Image src={organizationLogo} style={styles.logoImage} />
            )}
          </View>
          <View style={styles.orgBlock}>
            <Text style={styles.orgName}>
              {toDisplayString(organization?.organization_name, "Organization Name")}
            </Text>
            <Text style={styles.orgSubText}>
              {buildAddress(
                organization?.business_address,
                organization?.state,
                organization?.pincode
              )}
            </Text>
            {organization?.cin && (
              <Text style={styles.orgSubText}>
                CIN: {toDisplayString(organization?.cin)}
              </Text>
            )}
            {organization?.gst_number && (
              <Text style={styles.orgSubText}>
                GSTIN: {toDisplayString(organization?.gst_number)}
              </Text>
            )}
          </View>
        </View>

        <View style={styles.titleBlock}>
          <View style={styles.titleRow}>
            <Text style={styles.titleText}>{getReceiptTitle(type)}</Text>
          </View>
        </View>

        <View style={styles.metaBlock}>
          <MetaLine label="Invoice Number" value={receiptNumber} />
          <MetaLine label="Invoice Date" value={invoiceDate} />
          {renderBody()}
        </View>

        <View style={styles.sectionBlock}>
          <Text style={styles.sectionLabel}>Remarks:</Text>
          <Text style={styles.remarksText}>{remarks || "N/A"}</Text>
        </View>

        <View fixed style={styles.footer}>
          <View style={styles.footerLeft}>
            <View style={styles.footerLine}>
              <EmailIcon />
              <Text style={styles.footerText}>
                {toDisplayString(organization?.user_email)}
              </Text>
            </View>
          </View>
          <View style={styles.footerRight}>
            <View style={styles.footerLine}>
              <PhoneIcon />
              <Text style={styles.footerText}>
                {toDisplayString(organization?.contact_number)}
              </Text>
            </View>
            <View style={styles.footerLine}>
              <WebIcon />
              <Text style={styles.footerText}>
                {toDisplayString(organization?.website)}
              </Text>
            </View>
          </View>
        </View>
      </Page>

      {attachmentImages.map((imgSrc, index) => (
        <Page key={`${imgSrc}-${index}`} size="A4" style={styles.attachmentPage}>
          <Image src={imgSrc} style={styles.attachmentImage} />
        </Page>
      ))}
    </Document>
  );
};

export default ReceiptPDFDocument;
